<template>
  <div class="modal_wrapper active">
    <div class="modal_window white_box create_report_modal">
      <button
        class="close_modal close_btn"
        @click="closeModal()"
      >
        <svg
          fill="none"
          height="32"
          viewBox="0 0 32 32"
          width="32"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M25 7L7 25"
            stroke="#040707"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
          <path
            d="M25 25L7 7"
            stroke="#040707"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
        </svg>
      </button>

      <h3 class="heading_h4 font_bold mb_16">
        {{ widgetModal.widget ? 'Edit' : 'Create' }} Backlinks Widget
      </h3>

      <div :class="'section_wrap '+(settings === 'widget' ? 'open' : '')">
        <div
          class="section_head"
          @click="(settings === 'widget' ? settings = '' : settings = 'widget')"
        >
          <h3 class="section_heading">
            Widget Settings
          </h3>
          <i class="icon arrow_down_icon caret" />
        </div>
        <accordion-transition>
          <div
            v-if="settings === 'widget'"
            class="section_body"
          >
            <custom-input
              v-model="widget.name"
              label="Widget name"
              placeholder="Enter a name for the widget (optional)"
              width="full"
            />

            <custom-select
              v-model="widget.attributes.options.focus"
              :options="{
                'backlinks': 'Website Backlinks',
                'flow': 'Domain Authority'
              }"
              :width="widget.attributes.options.focus === 'backlinks' ? 'full' : 'half'"
              label="Focus"
              placeholder="Select a metric"
            />

            <custom-select
              v-if="widget.attributes.options.focus === 'flow'"
              v-model="widget.attributes.type"
              :options="{
                'line-chart': 'Line chart',
                'area-chart': 'Area chart',
                'table': 'Table',
                'stat-boxes': 'Stat boxes'
              }"
              label="Widget Type"
              width="half"
            />

            <div
              v-if="warning"
              class="text-center"
              style="width: 100%; margin-top: -10px; margin-bottom: 10px;"
            >
              <small class="text-danger">Domain Authority data is only run once a month. If the date the data was ran
                doesn't fall within the selected date range, no data will be returned.</small>
            </div>

            <custom-select
              v-model="widget.attributes.options.useDatesFrom"
              :options="dateRanges"
              label="Use date range"
              width="full"
            />

            <div
              v-if="widget.attributes.options.useDatesFrom === 'custom'"
              class="custom_daterange"
            >
              <custom-input
                :disabled="true"
                :placeholder="selectedDateRange(widget.attributes.options.dates.fromDate, widget.attributes.options.dates.toDate)"
                label="Date range"
                width="half"
              />

              <button
                class="btn secondary_btn open-datepicker half"
                @click="datepicker = true"
              >
                <span>Select Date Range</span>
              </button>

              <date-range-picker
                v-if="datepicker"
                v-model:compFromDate="widget.attributes.options.dates.compFromDate"
                v-model:compRange="widget.attributes.options.dates.compRange"
                v-model:compToDate="widget.attributes.options.dates.compToDate"
                v-model:dateRange="widget.attributes.options.dates.dateRange"
                v-model:fromDate="widget.attributes.options.dates.fromDate"
                v-model:toDate="widget.attributes.options.dates.toDate"
                :allow-compare="widget.attributes.type === 'stat-boxes'"
                @close="datepicker = false"
              />
            </div>

            <custom-select
              v-if="widget.attributes.options.focus === 'backlinks'"
              v-model="widget.attributes.options.metric"
              :options="{
                live: 'Live Backlinks',
                lost: 'Lost Backlinks',
                disavowed: 'Disavowed Backlinks'
              }"
              label="Metric"
              width="half"
            />

            <custom-multiselect
              v-if="widget.attributes.options.focus === 'flow' && (widget.attributes.type.includes('chart') || widget.attributes.type === 'stat-boxes')"
              v-model="widget.attributes.options.datasets"
              :options="{
                score: 'Domain Authority',
                trust: 'Trust Flow',
                citation: 'Citation Flow',
              }"
              label="Datasets"
              mode="tags"
              width="full"
            />

            <custom-input
              v-if="widget.attributes.type === 'table'"
              v-model="widget.attributes.options.limit"
              :width="widget.attributes.options.focus === 'flow' && widget.attributes.type === 'table' ? 'third' : 'half'"
              label="Limit results to"
              placeholder="All results"
              type="number"
            />

            <custom-select
              v-if="widget.attributes.type === 'table'"
              v-model="widget.attributes.options.sortBy"
              :options="visibleColumns"
              :width="widget.attributes.options.focus === 'flow' && widget.attributes.type === 'table' ? 'third' : 'half'"
              label="Sort by"
            />

            <custom-select
              v-if="widget.attributes.type === 'table'"
              v-model="widget.attributes.options.sortDirection"
              :options="{
                desc: 'Descending',
                asc: 'Ascending',
              }"
              :width="widget.attributes.options.focus === 'flow' && widget.attributes.type === 'table' ? 'third' : 'half'"
              label="Sort direction"
            />

            <custom-multiselect
              v-if="widget.attributes.type.includes('table')"
              v-model="widget.attributes.options.visibleColumns"
              :options="visibleColumns"
              label="Visible Columns"
              mode="tags"
              width="full"
            />
          </div>
        </accordion-transition>
      </div>

      <div
        v-if="widget.attributes.type.includes('chart')"
        :class="'section_wrap '+(settings === 'chart' ? 'open' : '')"
      >
        <div
          class="section_head"
          @click="(settings === 'chart' ? settings = '' : settings = 'chart')"
        >
          <h3 class="section_heading">
            Chart Settings <small>(optional)</small>
          </h3>
          <i class="icon arrow_down_icon caret" />
        </div>
        <accordion-transition>
          <div
            v-if="settings === 'chart'"
            class="section_body"
          >
            <custom-select
              v-model="widget.attributes.options.useColorsFrom"
              :options="{
                'report' : 'Use Report Colors',
                'custom' : 'Use Custom Colors',
              }"
              label="Chart Colors"
              width="full"
            />

            <custom-color-multiselect
              v-if="widget.attributes.options.useColorsFrom === 'custom'"
              v-model="widget.attributes.chartOptions.colors"
              label="Custom Colors"
              width="full"
            />

            <div class="chart_legend">
              <div class="toggles">
                <custom-toggle
                  v-model="widget.attributes.chartOptions.legend.show"
                  label="Show Legend"
                  width="quarter"
                />
              </div>

              <custom-select
                v-if="widget.attributes.chartOptions.legend.show"
                v-model="widget.attributes.chartOptions.legend.position"
                :options="{
                  'bottom': 'Bottom',
                  'top': 'Top',
                  'right': 'Right',
                  'left': 'Left'
                }"
                label="Legend position"
                width="half"
              />

              <custom-select
                v-if="widget.attributes.chartOptions.legend.show"
                v-model="widget.attributes.chartOptions.legend.horizontalAlign"
                :options="{
                  'center': 'Centre',
                  'right': 'Right',
                  'left': 'Left'
                }"
                label="Legend align"
                width="half"
              />
            </div>

            <div class="toggles">
              <custom-toggle
                v-model="widget.attributes.chartOptions.dataLabels.enabled"
                label="Show Data Labels"
                width="quarter"
              />

              <custom-toggle
                v-model="widget.attributes.chartOptions.grid.xaxis.lines.show"
                label="Show X Axis Grid"
                width="quarter"
              />

              <custom-toggle
                v-model="widget.attributes.chartOptions.grid.yaxis.lines.show"
                label="Show Y Axis Grid"
                width="quarter"
              />
            </div>
          </div>
        </accordion-transition>
      </div>

      <div
        v-if="widget.attributes.type === 'table'"
        :class="'section_wrap '+(settings === 'table' ? 'open' : '')"
      >
        <div
          class="section_head"
          @click="(settings === 'table' ? settings = '' : settings = 'table')"
        >
          <h3 class="section_heading">
            Table Settings <small>(optional)</small>
          </h3>
          <i class="icon arrow_down_icon caret" />
        </div>
        <accordion-transition>
          <div
            v-if="settings === 'table'"
            class="section_body"
          >
            <custom-select
              v-model="widget.attributes.tableOptions.align"
              :options="{
                'center' : 'Center',
                'start' : 'Left',
                'end' : 'Right'
              }"
              label="Text Align"
              width="full"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.headerColor"
              label="Header color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.headerTextColor"
              label="Header text color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.primaryRowColor"
              label="Primary row color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.primaryRowTextColor"
              label="Primary row text color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.secondaryRowColor"
              label="Secondary row color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.secondaryRowTextColor"
              label="Secondary row text color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />
          </div>
        </accordion-transition>
      </div>

      <div class="flex items-center section_modal_footer">
        <button
          class="btn btn_md secondary_btn close_modal ml_auto mr_8"
          @click="closeModal()"
        >
          <span>Cancel</span>
        </button>
        <button
          class="btn btn_md primary_btn"
          @click="save()"
        >
          <span>{{ widgetModal.widget ? 'Update' : 'Create' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import {mapActions, mapState} from 'vuex';
import CustomInput from '../../global/forms/CustomInput.vue';
import CustomSelect from '../../global/forms/CustomSelect.vue';
import CustomColorSelect from '../../global/forms/CustomColorSelect.vue';
import CustomMultiselect from '../../global/forms/CustomMultiselect.vue';
import CustomColorMultiselect from '../../global/forms/CustomColorMultiselect.vue';
import CustomToggle from '../../global/forms/CustomToggle.vue';
import DateRangePicker from '../../global/forms/DateRangePicker.vue';
import AccordionTransition from '../../global/transitions/AccordionTransition.vue';

export default {
  components: {
    CustomInput,
    CustomSelect,
    CustomColorSelect,
    CustomMultiselect,
    CustomColorMultiselect,
    CustomToggle,
    DateRangePicker,
    AccordionTransition
  },
  emits: ['refresh-widget'],
  data() {
    return {
      datepicker: false,
      settings: 'widget',
      widget: {
        id: `widget-backlinks-${Date.now()}`,
        name: 'Backlinks Widget',
        type: 'backlinks',
        w: 12,
        h: 25,
        frozen: false,
        content: `<div id="widget-backlinks-${Date.now()}"><div class="widget-loading"><i class="fa fa-btn fa-spinner fa-spin"></i></div></div>`,
        attributes: {
          type: 'table',
          options: {
            focus: 'backlinks',
            metric: 'live',
            datasets: ['score', 'trust', 'citation'],
            limit: null,
            sortBy: 'score',
            sortDirection: 'desc',
            useColorsFrom: 'report',
            useDatesFrom: 'all_dates',
            dates: {
              dateRange: null,
              compRange: null,
              fromDate: '',
              toDate: '',
              compFromDate: '',
              compToDate: ''
            },
            visibleColumns: ['domain', 'score', 'count'],
          },
          tableOptions: {
            align: 'center',
            headerColor: '#F5F5F5',
            headerTextColor: '#4E4E54',
            primaryRowColor: '#FFFFFF',
            primaryRowTextColor: '#4E4E54',
            secondaryRowColor: '#F8F7F5',
            secondaryRowTextColor: '#4E4E54',
          },
          chartOptions: {
            chart: {
              id: `widget-backlinks-${Date.now()}`,
              animations: {
                enabled: false
              }
            },
            title: {
              text: '',
              align: 'center'
            },
            colors: [],
            grid: {
              xaxis: {
                lines: {
                  show: false
                }
              },
              yaxis: {
                lines: {
                  show: false
                }
              },
            },
            legend: {
              show: true,
              position: 'top',
              horizontalAlign: 'center'
            },
            dataLabels: {
              enabled: false
            }
          }
        }
      },
      warning: false,
    }
  },
  computed: {
    ...mapState(['widgetModal']),
    ...mapState('report', ['report']),
    visibleColumns() {
      if (this.widget.attributes.options.focus === 'backlinks') {
        return {
          domain: 'Domain Name',
          score: 'Domain Authority',
          count: 'Number of Backlinks',
          date_found: 'Date Found',
          date_lost: 'Date Lost'
        };
      } else {
        return {
          date: 'Date',
          score: 'Domain Authority',
          trust_flow: 'Trust Flow',
          citation_flow: 'Citation Flow',
          ext_back_links: 'External Backlinks',
          ref_domains: 'Referring Domains'
        };
      }
    },
    dateRanges() {
      if (this.widget.attributes.type !== 'stat-boxes') {
        return {
          all_dates: 'All dates',
          report: 'Report date range',
          custom: 'Custom date range'
        }
      } else {
        return {
          report: 'Report date range',
          custom: 'Custom date range'
        }
      }
    }
  },
  watch: {
    'widget.attributes.type'(newVal) {
      if (newVal === 'stat-boxes') {
        this.widget.attributes.options.useDatesFrom = this.widgetModal.widget ? this.widgetModal.widget.attributes.options.useDatesFrom : 'report';
      }
    }
  },
  beforeMount() {
    if (this.widgetModal.widget) {
      _.merge(this.widget, this.widgetModal.widget);
      this.widget.attributes.options.visibleColumns = this.widgetModal.widget.attributes.options.visibleColumns;
      this.widget.attributes.options.datasets = this.widgetModal.widget.attributes.options.datasets;
      this.warning = this.widgetModal.widget.attributes.options.focus === 'flow';
    }
  },
  mounted() {
    // Adding watch when mounted so its not triggered on initial load
    this.$watch('widget.attributes.options.focus', function (newVal) {
      this.updateSelects(newVal);
    });
  },
  methods: {
    ...mapActions('pages', ['addWidget', 'updateWidget']),
    ...mapActions('widgetModal', ['closeModal']),
    selectedDateRange(fromDate, toDate) {
      if (fromDate && toDate) {
        let from = moment(fromDate, 'YYYY-MM-DD').format(this.report.date_format);
        let to = moment(toDate, 'YYYY-MM-DD').format(this.report.date_format);
        return from + ' - ' + to;
      }
      return 'Please select dates';
    },
    async save() {
      if (!this.widget.resized && !this.widgetModal.widget) {
        if (this.widget.attributes.type.includes('table') || this.widget.attributes.type.includes('stat-boxes')) this.widget.w = 12;
      }

      // Prep a payload for the vuex action
      const payload = {
        widget: this.widget,
        pageId: this.widgetModal.pageId,
      }

      // Check if we're creating a new widget or editing one
      if (!this.widgetModal.widget) {
        // Save the new pages layout (refreshes store pages)
        await this.addWidget(payload);
      } else {
        // Update the widget
        payload.widget.resized = false;
        await this.updateWidget(payload);
      }

      // Emit to trigger widget refresh, then close the modal
      this.$emit('refresh-widget', {pageId: this.widgetModal.pageId, widget: this.widget});
      this.closeModal();
    },
    // Update the select options based on the focus
    updateSelects(focus) {
      if (focus === 'backlinks') {
        this.widget.attributes.options.visibleColumns = [];
        this.widget.attributes.options.sortBy = 'date';
        this.widget.attributes.options.metric = 'live';
        this.widget.attributes.type = 'table';
        this.warning = false;
        this.$nextTick(() => {
          this.widget.attributes.options.sortBy = 'score';
          this.widget.attributes.options.visibleColumns = ['domain', 'score', 'count'];
        });

      } else {
        this.widget.attributes.options.visibleColumns = [];
        this.widget.attributes.options.sortBy = 'date';
        this.widget.attributes.type = 'line-chart';
        this.warning = true;
        this.$nextTick(() => {
          this.widget.attributes.options.sortBy = 'date';
          this.widget.attributes.options.visibleColumns = ['date', 'score', 'trust_flow', 'citation_flow'];
        });
      }
    }
  },
}
</script>

<style scoped src="../../../../reports/css/modal.css" />
